import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface FileDropzoneProps {
  /**
   * Called when a valid file is selected via click or drag-and-drop. In
   * `multiple` mode this fires for the FIRST validated file only, so a
   * single-file consumer can stay unchanged; prefer `onFilesSelect` when
   * `multiple` is set.
   */
  onFileSelect: (file: File) => void;
  /**
   * Accept several files at once. Sets the native input's `multiple`
   * attribute and routes drop/change through {@link onFilesSelect} with
   * every validated file.
   */
  multiple?: boolean;
  /**
   * Called with ALL validated files from a drop or change when `multiple` is
   * set. (In single-file mode only `onFileSelect` fires.)
   */
  onFilesSelect?: (files: File[]) => void;
  /** Accepted MIME types (e.g. ['application/pdf', 'image/jpeg']). */
  accept?: string[];
  /** Maximum file size in bytes. Defaults to 5 MB. */
  maxSizeBytes?: number;
  /** Whether the dropzone is disabled. */
  disabled?: boolean;
  /** Label for the drag-drop prompt. Required — no English fallback. */
  promptText: string;
  /** Subtext below the prompt (e.g. 'PDF, JPEG, PNG up to 5 MB'). */
  subText?: string;
  /** Label for the browse-files link. Required — no English fallback. */
  browseText: string;
  /** Error message to display when a file fails validation. */
  onError?: (message: string) => void;
  /**
   * Builds the localized "unsupported file type" message. Required so the
   * component never emits a hardcoded English error.
   */
  unsupportedTypeError: (fileType: string) => string;
  /**
   * Builds the localized "file too large" message. Required so the component
   * never emits a hardcoded English error.
   */
  tooLargeError: (maxMb: string) => string;
  className?: string;
}

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

export function FileDropzone({
  onFileSelect,
  multiple = false,
  onFilesSelect,
  accept,
  maxSizeBytes = DEFAULT_MAX_BYTES,
  disabled = false,
  promptText,
  subText,
  browseText,
  onError,
  unsupportedTypeError,
  tooLargeError,
  className,
}: FileDropzoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = useCallback(
    (file: File): boolean => {
      if (accept && accept.length > 0 && !accept.includes(file.type)) {
        onError?.(unsupportedTypeError(file.type));
        return false;
      }
      if (file.size > maxSizeBytes) {
        const mb = (maxSizeBytes / (1024 * 1024)).toFixed(0);
        onError?.(tooLargeError(mb));
        return false;
      }
      return true;
    },
    [accept, maxSizeBytes, onError, unsupportedTypeError, tooLargeError],
  );

  // Route a picked FileList to the right callback(s). In single-file mode only
  // the first valid file is emitted via onFileSelect (unchanged behaviour). In
  // multiple mode every valid file goes to onFilesSelect, and onFileSelect
  // still fires for the first so a mixed consumer keeps working.
  const emit = useCallback(
    (fileList: FileList | null) => {
      // `Array.from` works on both a real FileList and the array-like the test
      // harness supplies, so we never depend on `.item()` existing.
      const files = fileList ? Array.from(fileList) : [];
      if (files.length === 0) return;
      if (!multiple) {
        const first = files[0];
        if (validate(first)) onFileSelect(first);
        return;
      }
      const valid = files.filter((file) => validate(file));
      if (valid.length === 0) return;
      onFileSelect(valid[0]);
      onFilesSelect?.(valid);
    },
    [multiple, validate, onFileSelect, onFilesSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled) return;
      emit(e.dataTransfer.files);
    },
    [disabled, emit],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      emit(e.target.files);
      // Reset so the same file can be re-selected after an error
      e.target.value = '';
    },
    [emit],
  );

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors',
        isDragOver && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-divider hover:border-primary/50',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragOver(true);
      }}
      onDragLeave={() => {
        setIsDragOver(false);
      }}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          inputRef.current?.click();
        }
      }}
    >
      <Upload className="h-10 w-10 text-ink-muted" aria-hidden />
      <div>
        <p className="text-sm font-medium text-ink">{promptText}</p>
        {subText && <p className="mt-0.5 text-xs text-ink-muted">{subText}</p>}
      </div>
      <span className="text-xs font-medium text-primary underline-offset-2 hover:underline">
        {browseText}
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={accept?.join(',')}
        multiple={multiple}
        className="sr-only"
        disabled={disabled}
        onChange={handleChange}
        aria-hidden
      />
    </div>
  );
}
